import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, FileText, FileDown, ExternalLink, Loader2, Receipt, Coins, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * "Mijn Facturen" — historical overview of all credit purchases.
 *
 * - Regular makelaars see only their own invoices (read-only).
 * - Admins can pass ``?user_id=<id>`` to inspect any user's invoices and
 *   get an extra "Verwijderen"-knop per row that wipes the purchase row
 *   from the platform DB and reverses the credit grant.
 */
export default function MyInvoices() {
    const { user: me } = useAuth();
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const targetUserId = searchParams.get("user_id");

    const [invoices, setInvoices] = useState([]);
    const [scopedUser, setScopedUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const url = targetUserId ? `/credits/invoices?user_id=${encodeURIComponent(targetUserId)}` : "/credits/invoices";
            const { data } = await api.get(url);
            setInvoices(data.invoices || []);
            setScopedUser(data.user || null);
        } catch (e) {
            setError(e.response?.data?.detail || "Kon facturen niet ophalen");
        } finally {
            setLoading(false);
        }
    }, [targetUserId]);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    const isAdminView = me?.role === "admin" && scopedUser && !scopedUser.is_self;
    const totalCredits = invoices.reduce((s, x) => s + (x.credits || 0), 0);
    const totalSpent = invoices.reduce((s, x) => s + (x.amount_eur || 0), 0);

    const performDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const { data } = await api.delete(`/admin/credit-purchases/${deleteTarget.session_id}`);
            toast.success(
                data.credits_reversed > 0
                    ? `${t("common.success")} · ${data.credits_reversed} ${t("common.credits")}`
                    : t("common.success")
            );
            setDeleteTarget(null);
            await fetchInvoices();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("common.error"));
        } finally {
            setDeleting(false);
        }
    };

    const backLink = isAdminView && scopedUser ? `/admin/users/${scopedUser.id}` : "/tools";
    const backLabel = isAdminView ? t("invoices.back_to_user") : t("invoices.back_to_tools");

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="my-invoices-page">
            <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
                <Link
                    to={backLink}
                    data-testid="my-invoices-back-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-950 transition mb-8"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> {backLabel}
                </Link>

                <div className="flex items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#22c55e] mb-3 inline-flex items-center gap-2">
                            <Receipt className="h-3.5 w-3.5" /> {isAdminView ? t("invoices.eyebrow_admin") : t("invoices.eyebrow_user")}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                            {isAdminView ? t("invoices.title_admin", { name: scopedUser.name || scopedUser.email }) : t("invoices.title")}
                        </h1>
                        <p className="mt-3 text-sm text-zinc-500 max-w-xl">
                            {isAdminView ? t("invoices.subtitle_admin") : t("invoices.subtitle_user")}
                        </p>
                    </div>
                    {invoices.length > 0 && (
                        <div className="hidden md:flex items-stretch divide-x divide-zinc-200 bg-white border border-zinc-200 rounded-sm overflow-hidden">
                            <div className="px-6 py-4">
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("invoices.stats_purchases")}</p>
                                <p className="text-2xl font-semibold tabular-nums">{invoices.length}</p>
                            </div>
                            <div className="px-6 py-4">
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("invoices.stats_credits")}</p>
                                <p className="text-2xl font-semibold tabular-nums text-[#22c55e]">{totalCredits}</p>
                            </div>
                            <div className="px-6 py-4">
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("invoices.stats_total")}</p>
                                <p className="text-2xl font-semibold tabular-nums text-[#E5A93D]">€{totalSpent.toFixed(2)}</p>
                            </div>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className="flex items-center gap-3 text-sm text-zinc-500" data-testid="my-invoices-loading">
                        <Loader2 className="h-4 w-4 animate-spin" /> {t("invoices.loading")}
                    </div>
                )}

                {!loading && error && (
                    <div className="bg-red-500/[0.06] border border-red-500/30 rounded-sm p-6 text-sm text-red-700" data-testid="my-invoices-error">
                        {error}
                    </div>
                )}

                {!loading && !error && invoices.length === 0 && (
                    <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center" data-testid="my-invoices-empty">
                        <FileText className="h-10 w-10 mx-auto text-zinc-400 mb-4" />
                        <p className="text-sm text-zinc-600 mb-1">{t("invoices.empty_title")}</p>
                        <p className="text-xs text-zinc-500 mb-6">
                            {isAdminView ? t("invoices.empty_desc_admin") : t("invoices.empty_desc_user")}
                        </p>
                        {!isAdminView && (
                            <Link
                                to="/tools"
                                className="inline-flex items-center gap-2 h-10 px-5 rounded-sm bg-white text-zinc-950 font-bold text-sm hover:bg-zinc-100 transition"
                                data-testid="my-invoices-buy-cta"
                            >
                                <Coins className="h-4 w-4" /> {t("invoices.buy_credits")}
                            </Link>
                        )}
                    </div>
                )}

                {!loading && !error && invoices.length > 0 && (
                    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="my-invoices-table">
                        <div className={`hidden md:grid gap-4 px-6 py-3 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-white ${isAdminView ? "grid-cols-[1.1fr_0.9fr_0.7fr_0.9fr_1.1fr_0.6fr]" : "grid-cols-[1.2fr_1fr_0.8fr_1fr_1.2fr]"}`}>
                            <span>{t("invoices.table_date")}</span>
                            <span>{t("invoices.table_package")}</span>
                            <span className="text-right">{t("invoices.table_credits")}</span>
                            <span className="text-right">{t("invoices.table_amount")}</span>
                            <span className="text-right">{t("invoices.table_invoice")}</span>
                            {isAdminView && <span className="text-right">{t("invoices.table_action")}</span>}
                        </div>
                        {invoices.map((inv) => (
                            <div
                                key={inv.session_id}
                                data-testid={`invoice-row-${inv.session_id}`}
                                className={`grid grid-cols-1 gap-3 md:gap-4 px-6 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-100 transition ${isAdminView ? "md:grid-cols-[1.1fr_0.9fr_0.7fr_0.9fr_1.1fr_0.6fr]" : "md:grid-cols-[1.2fr_1fr_0.8fr_1fr_1.2fr]"}`}
                            >
                                <div className="text-sm">
                                    <p className="text-zinc-950 font-medium tabular-nums">{formatDate(inv.credited_at || inv.created_at, i18n.language)}</p>
                                    <p className="text-[10px] text-zinc-400 md:hidden uppercase tracking-wider mt-0.5">{t("invoices.table_date")}</p>
                                </div>
                                <div className="text-sm">
                                    <p className="text-zinc-950">{inv.package_label}</p>
                                    <p className="text-[10px] text-zinc-400 md:hidden uppercase tracking-wider mt-0.5">{t("invoices.table_package")}</p>
                                </div>
                                <div className="text-sm md:text-right">
                                    <p className="text-[#22c55e] font-semibold tabular-nums">+{inv.credits}</p>
                                    <p className="text-[10px] text-zinc-400 md:hidden uppercase tracking-wider mt-0.5">{t("invoices.table_credits")}</p>
                                </div>
                                <div className="text-sm md:text-right">
                                    <p className="text-zinc-950 font-semibold tabular-nums">€{(inv.amount_eur || 0).toFixed(2)}</p>
                                    <p className="text-[10px] text-zinc-500 md:hidden uppercase tracking-wider mt-0.5">{t("common.excl_vat")}</p>
                                </div>
                                <div className="flex md:justify-end items-center gap-2 flex-wrap">
                                    {inv.invoice_pdf ? (
                                        <a
                                            href={inv.invoice_pdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            data-testid={`invoice-download-${inv.session_id}`}
                                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-white text-zinc-950 text-xs font-bold hover:bg-zinc-100 transition"
                                        >
                                            <FileDown className="h-3.5 w-3.5" /> PDF
                                        </a>
                                    ) : (
                                        <span className="text-[11px] text-zinc-500" data-testid={`invoice-unavailable-${inv.session_id}`}>{t("invoices.unavailable")}</span>
                                    )}
                                    {inv.hosted_invoice_url && (
                                        <a
                                            href={inv.hosted_invoice_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`invoice-view-${inv.session_id}`}
                                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-zinc-200 text-zinc-950 text-xs font-medium hover:bg-zinc-100 transition"
                                        >
                                            <ExternalLink className="h-3.5 w-3.5" /> {t("common.view")}
                                        </a>
                                    )}
                                </div>
                                {isAdminView && (
                                    <div className="flex md:justify-end items-center">
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget(inv)}
                                            data-testid={`invoice-delete-${inv.session_id}`}
                                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-rose-500/30 text-rose-700 text-xs font-medium hover:bg-rose-500/10 hover:border-rose-500/50 transition"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!loading && !error && invoices.length > 0 && !isAdminView && (
                    <p className="mt-6 text-xs text-zinc-500" data-testid="my-invoices-footer-note">
                        {t("invoices.footer_note")} <a href="mailto:info@social-factory.be" className="text-[#E5A93D] underline underline-offset-2">info@social-factory.be</a>.
                    </p>
                )}
            </div>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="invoice-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("invoices.delete_dialog_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("invoices.delete_dialog_desc", {
                                pkg: deleteTarget?.package_label,
                                credits: deleteTarget?.credits,
                                amount: (deleteTarget?.amount_eur || 0).toFixed(2),
                                name: scopedUser?.name || scopedUser?.email || "",
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="invoice-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); performDelete(); }}
                            disabled={deleting}
                            data-testid="invoice-delete-confirm"
                            className="bg-rose-600 hover:bg-rose-500"
                        >
                            {deleting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("tools.buying")}</>) : (<><Trash2 className="h-4 w-4 mr-2" /> {t("common.delete")}</>)}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function formatDate(iso, lang) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const locale = lang === "fr" ? "fr-BE" : lang === "en" ? "en-GB" : "nl-BE";
        return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}
